import React from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://businessdevelopmentauthority.net';

const ProcessAutomationPage: React.FC = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/process-automation`,
        'url': `${SITE_URL}/process-automation`,
        'name': 'Process Automation Services - Business Development Authority',
        'description': 'Streamline operations and boost efficiency with intelligent process automation solutions — RPA, workflow automation, document processing, and system integration.',
        'isPartOf': { '@id': SITE_URL },
      },
      {
        '@type': 'Service',
        'name': 'Process Automation',
        'url': `${SITE_URL}/process-automation`,
        'description': 'Intelligent automation solutions that eliminate repetitive manual tasks, reduce errors, and free up valuable time for strategic initiatives.',
        'serviceType': 'Robotic Process Automation',
        'provider': {
          '@type': 'Organization',
          'name': 'Business Development Authority',
          'url': SITE_URL,
        },
        'areaServed': 'US',
        'hasOfferCatalog': {
          '@type': 'OfferCatalog',
          'name': 'Automation Services',
          'itemListElement': [
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Robotic Process Automation' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Workflow Automation' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Data Processing Automation' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Document Processing' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'System Integration' } },
          ],
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <div className="min-h-screen">
        <Header />
        
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center justify-center bg-cover bg-center" style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/487a7f9418b6ca4d36c3e0a2587921dc.jpg')` }}>
          <div className="max-w-7xl mx-auto px-6 text-center text-white w-full">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Process Automation</h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">Streamline operations and boost efficiency with intelligent automation solutions that transform your business processes</p>
            <button onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })} className="whitespace-nowrap bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors cursor-pointer">
              Automate Your Processes
            </button>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">Transform Manual Tasks into Automated Workflows</h2>
                <p className="text-lg text-gray-700 mb-6">
                  Our Process Automation services help businesses eliminate repetitive manual tasks, reduce errors, and free up valuable time for strategic initiatives. We design and implement intelligent automation solutions that integrate seamlessly with your existing systems.
                </p>
                <p className="text-lg text-gray-700 mb-6">
                  From simple task automation to complex workflow orchestration, we deliver solutions that drive operational excellence and measurable cost savings.
                </p>
              </div>
              <div className="relative h-[400px] rounded-lg overflow-hidden shadow-2xl">
                <img src="https://readdy.ai/api/search-image?query=Modern%20office%20workspace%20with%20automated%20digital%20workflows%20displayed%20on%20multiple%20screens%2C%20robotic%20process%20automation%20interface%20with%20data%20processing%2C%20professional%20technology%20environment%20with%20clean%20design&width=600&height=400&seq=automation-overview&orientation=landscape" alt="Process Automation" className="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
        </section>

        {/* Automation Areas Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-12">Automation Solutions</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-robot-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Robotic Process Automation</h3>
                <p className="text-gray-700">Deploy software robots to handle repetitive tasks, data entry, and rule-based processes with speed and accuracy.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-flow-chart text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Workflow Automation</h3>
                <p className="text-gray-700">Streamline complex business processes with intelligent workflow automation that connects systems and teams.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-database-2-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Data Processing</h3>
                <p className="text-gray-700">Automate data collection, validation, transformation, and reporting for faster insights and decision-making.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-mail-send-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Communication Automation</h3>
                <p className="text-gray-700">Automate email responses, notifications, and customer communications for improved engagement.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-file-list-3-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Document Processing</h3>
                <p className="text-gray-700">Intelligent document extraction, classification, and processing using AI-powered OCR and NLP technologies.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-links-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">System Integration</h3>
                <p className="text-gray-700">Connect disparate systems and automate data synchronization across your technology stack.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-12">Automation Benefits</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                  <i className="ri-time-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Time Savings</h3>
                  <p className="text-gray-700">Reduce task completion time by up to 80% and free your team for higher-value activities.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                  <i className="ri-error-warning-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Error Reduction</h3>
                  <p className="text-gray-700">Eliminate human errors in repetitive tasks and ensure consistent, accurate results.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Cost Efficiency</h3>
                  <p className="text-gray-700">Reduce operational costs and achieve rapid ROI through process optimization.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                  <i className="ri-line-chart-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Scalability</h3>
                  <p className="text-gray-700">Scale operations effortlessly without proportional increases in headcount or costs.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="contact" className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-6">Ready to Automate Your Processes?</h2>
            <p className="text-xl mb-8">Let's identify automation opportunities and transform your operations for maximum efficiency.</p>
            <button onClick={() => window.REACT_APP_NAVIGATE('/calendar')} className="whitespace-nowrap bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold transition-colors cursor-pointer">
              Schedule a Consultation
            </button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default ProcessAutomationPage;
